import client from '@/services/graphql/client';
import { ApolloError, useQuery } from '@apollo/client';
import { usePersistedCacheQuery } from '@common/apollo';
import {
    MerchantDocument,
    MerchantFieldsFragment,
    SearchTransactionEntriesDocument,
    TransactionEntriesFilteredDocument,
    TransactionEntryCategoryFieldsFragment,
} from '@common/graphql/generated';
import {
    buildMerchants,
    buildTransactionEntriesFilter,
    CashflowFinancialEntity,
    CashflowTransactionEntry,
    filterTransactionEntries,
    UNKNOWN_MERCHANT_ID,
    useCashflowPushEventHandler,
    useFinancialEntityMap,
    useTransactionEntries,
    useTransactionEntryCategories,
} from '@common/react/cashflow';
import { useHasNoFEs } from '@common/react/financialEntities';
import { getSampleTransactionEntries } from '@common/sampleData';
import { DateRange } from '@common/types';
import { dt, getRangeUnit, isAllTimeDateRange } from '@common/util/dateTime';
import {
    parseAsArrayOf,
    parseAsString,
    useQueryState,
    useQueryStates,
    UseQueryStatesOptions,
} from 'next-usequerystate';
import { createContext, useContext, useMemo } from 'react';

interface CashflowContext {
    transactionEntries: CashflowTransactionEntry[];
    historicalTransactionEntries: CashflowTransactionEntry[];
    loading: boolean;
    error: ApolloError | undefined;
    dateRange: DateRange;
    setDateRange: (dateRange: DateRange) => void;
    categoryIds: string[] | null;
    setCategoryIds: (categoryIds: string[] | null) => void;
    accountIds: string[] | null;
    setAccountIds: (accountIds: string[] | null) => void;
    tagNames: string[] | null;
    setTagNames: (tagNames: string[] | null) => void;
    setNextDateRange: (add: boolean) => void;
    merchantName: string | null;
    setMerchantId: (merchantId: string | null) => void;
    merchantId: string | null;
    merchants: MerchantFieldsFragment[];
    financialEntities: CashflowFinancialEntity[];
    transactionEntryCategories: TransactionEntryCategoryFieldsFragment[];
    transactionEntryCategoriesMapById: Record<
        string,
        TransactionEntryCategoryFieldsFragment
    >;
    query: string | null;
    setQuery: (query: string | null) => void;
    isAllTimeDateRange: boolean;
    areFiltersApplied: boolean;
}

const Ctx = createContext<CashflowContext>({
    transactionEntries: [],
    historicalTransactionEntries: [],
    loading: false,
    error: undefined,
    dateRange: { startDate: '', endDate: '' },
    setDateRange: () => {},
    categoryIds: [],
    setCategoryIds: () => {},
    accountIds: [],
    setAccountIds: () => {},
    tagNames: [],
    setTagNames: () => {},
    setNextDateRange: () => {},
    merchantName: null,
    setMerchantId: () => {},
    financialEntities: [],
    transactionEntryCategories: [],
    transactionEntryCategoriesMapById: {},
    merchants: [],
    merchantId: null,
    query: null,
    setQuery: () => {},
    isAllTimeDateRange: false,
    areFiltersApplied: false,
});

// These options allow the back button to work as expected when filters/date range are changed
const ROUTING_OPTIONS = {
    history: 'push',
    shallow: false,
} satisfies UseQueryStatesOptions;

const today = dt();

export const CashflowProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const [dateRange, setDateRange] = useQueryStates(
        {
            startDate: parseAsString.withDefault(
                today.startOf('month').toDateStr()
            ),
            endDate: parseAsString.withDefault(
                today.endOf('month').toDateStr()
            ),
        },
        ROUTING_OPTIONS
    );

    const [categoryIds, setCategoryIds] = useQueryState(
        'categoryIds',
        parseAsArrayOf(parseAsString).withOptions(ROUTING_OPTIONS)
    );
    const [accountIds, setAccountIds] = useQueryState(
        'accountIds',
        parseAsArrayOf(parseAsString).withOptions(ROUTING_OPTIONS)
    );
    const [tagNames, setTagNames] = useQueryState(
        'tagNames',
        parseAsArrayOf(parseAsString).withOptions(ROUTING_OPTIONS)
    );
    const [merchantId, setMerchantId] = useQueryState(
        'merchantId',
        parseAsString.withOptions(ROUTING_OPTIONS)
    );
    const [query, setQuery] = useQueryState(
        'query',
        parseAsString.withOptions(ROUTING_OPTIONS)
    );

    const setNextDateRange = (add: boolean) => {
        if (isAllTimeDateRange(dateRange)) return;

        const unit = getRangeUnit(dateRange);

        if (unit === 'custom') {
            const days = dt(dateRange.endDate).diff(
                dateRange.startDate,
                'days'
            );
            const startDate = dt(dateRange.startDate).add(
                add ? days : -days,
                'days'
            );

            setDateRange({
                startDate: startDate.toDateStr(),
                endDate: startDate.add(days, 'days').toDateStr(),
            });
        } else {
            const startDate = dt(dateRange.startDate)
                .add(add ? 1 : -1, unit)
                .startOf(unit);

            setDateRange({
                startDate: startDate.toDateStr(),
                endDate: startDate.endOf(unit).toDateStr(),
            });
        }
    };

    const { data: merchantQuery, loading: merchantLoading } = useQuery(
        MerchantDocument,
        merchantId && merchantId !== UNKNOWN_MERCHANT_ID
            ? { variables: { id: merchantId } }
            : { skip: true }
    );

    const {
        data: transactionEntriesQuery,
        error,
        loading: transactionEntriesLoading,
    } = usePersistedCacheQuery(TransactionEntriesFilteredDocument, {
        variables: {
            filter: buildTransactionEntriesFilter({
                categoryIds,
                accountIds,
                merchantId,
                tagNames,
                dateRange,
            }),
        },
        fetchPolicy: 'cache-and-network',
    });

    const {
        data: transactionEntriesSearchQuery,
        error: transactionEntriesSearchError,
        loading: transactionEntriesSearchLoading,
    } = useQuery(
        SearchTransactionEntriesDocument,
        query ? { variables: { query } } : { skip: true }
    );

    const {
        transactionEntryCategories,
        transactionEntryCategoriesMapById,
        transactionEntryCategoriesLoading,
    } = useTransactionEntryCategories();

    const { financialEntityMap, financialEntityMapLoading } =
        useFinancialEntityMap();

    const hasNoFEs = useHasNoFEs();

    const allTransactionEntries = useTransactionEntries({
        transactionEntries: hasNoFEs
            ? getSampleTransactionEntries()
            : query
              ? transactionEntriesSearchQuery?.searchTransactionEntries
              : transactionEntriesQuery?.transactionEntries,
        financialEntityMap,
        transactionEntryCategoriesMapById,
    });

    const filteredTransactionEntries = useMemo(
        () =>
            filterTransactionEntries({
                allTransactionEntries,
                categoryIds,
                accountIds,
                tagNames,
                merchantId,
            }),
        [categoryIds, merchantId, accountIds, tagNames, allTransactionEntries]
    );

    const adjustedDateRange = useMemo(() => {
        // If we're viewing all time, expose the first transaction date as the start date and the end of the current month as the end date
        if (isAllTimeDateRange(dateRange) && allTransactionEntries.length) {
            return {
                startDate: dt(
                    allTransactionEntries[allTransactionEntries.length - 1].date
                )
                    .startOf('month')
                    .toDateStr(),
                endDate: today.endOf('month').toDateStr(),
            };
        }

        return dateRange;
    }, [allTransactionEntries, dateRange]);

    const transactionEntries = useMemo(
        () =>
            // When there's a query, show all transactions that match the query, regardless of the date range
            // Otherwise, show transactions that are within the selected date range
            // (since we fetch an extra 3 months of data for 3-month avg calculation)
            query
                ? filteredTransactionEntries
                : filteredTransactionEntries.filter(
                      (t) => adjustedDateRange.startDate <= t.date
                  ),
        [adjustedDateRange.startDate, filteredTransactionEntries, query]
    );

    const merchants = useMemo(
        () =>
            buildMerchants(
                // Only include merchants in the actual selected date range; ignore merchants from the historical query
                allTransactionEntries.filter(
                    (t) => adjustedDateRange.startDate <= t.date
                )
            ),
        [allTransactionEntries, adjustedDateRange.startDate]
    );

    useCashflowPushEventHandler({
        categoryIds,
        accountIds,
        tagNames,
        merchantId,
        dateRange,
        apolloClient: client,
    });

    return (
        <Ctx.Provider
            value={{
                transactionEntries,
                historicalTransactionEntries: filteredTransactionEntries, // filteredTransactionEntries contains an extra 3 months of data
                financialEntities: Object.values(financialEntityMap ?? {}),
                transactionEntryCategories,
                transactionEntryCategoriesMapById:
                    transactionEntryCategoriesMapById ?? {},
                loading:
                    financialEntityMapLoading ||
                    transactionEntriesLoading ||
                    transactionEntriesSearchLoading ||
                    transactionEntryCategoriesLoading ||
                    merchantLoading,
                error: error || transactionEntriesSearchError,
                dateRange: adjustedDateRange,
                setDateRange,
                categoryIds,
                setCategoryIds,
                accountIds,
                setAccountIds,
                tagNames,
                setTagNames,
                setNextDateRange,
                merchantName:
                    merchantId === UNKNOWN_MERCHANT_ID
                        ? 'Unknown'
                        : merchantQuery?.merchant?.name ?? null,
                setMerchantId,
                merchants,
                merchantId,
                query,
                setQuery,
                areFiltersApplied: !!(
                    accountIds ||
                    merchantId ||
                    categoryIds ||
                    tagNames ||
                    query
                ),
                isAllTimeDateRange: isAllTimeDateRange(dateRange),
            }}
        >
            {children}
        </Ctx.Provider>
    );
};

export const useCashflow = (): CashflowContext => {
    return useContext(Ctx);
};
